import LifeInsuredMedicalQuestionnaire from '../../models/APP/LifeInsuredMedicalQuestionnaire'
import QuestionUsage from '@/modules/common/common/models/APP/questionnaire/QuestionUsage'
import QuestionDescription from '@/modules/common/common/models/APP/questionnaire/QuestionDescription'
import QuestionAdmission from '@/modules/common/common/models/APP/questionnaire/QuestionAdmission'
import QuestionPathology from '@/modules/common/common/models/APP/questionnaire/QuestionPathology'
import type UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import type LifeHiringInsured from '../../models/APP/LifeHiringInsured'
import AssociatedQuestionnaire from '../../models/MID/AssociatedQuestionnaire'

const WEIGHT_QUESTION = 'P002'
const HEIGHT_QUESTION = 'P001'
const DISABILITY_QUESTION = 'P003'
const INTERRUPTED_ACTIVITY_QUESTION = 'P004'
const SICK_LEAVE_QUESTION = 'P005'
const SMOKING_QUESTION = 'P006'
const ALCOHOL_QUESTION = 'P007'
const DRUGS_QUESTION = 'P008'
const MEDICATION_QUESTION = 'P020'
const INJURIES_QUESTION = 'P044'

const MEASURE_QUESTIONS = [WEIGHT_QUESTION, HEIGHT_QUESTION]
const MEDICAL_QUESTIONS = [
  DISABILITY_QUESTION,
  INTERRUPTED_ACTIVITY_QUESTION,
  SICK_LEAVE_QUESTION,
  SMOKING_QUESTION,
  ALCOHOL_QUESTION,
  DRUGS_QUESTION,
  MEDICATION_QUESTION,
  INJURIES_QUESTION
]

export default class SaveQuestionnaireMapper {
  static toRequest(
    quotationId: string,
    questionnaire: LifeInsuredMedicalQuestionnaire,
    insured: LifeHiringInsured
  ) {
    let questions: any[] = []
    const measureQuestions = toMeasureQuestions(insured.userDetails)
    questions = questions.concat(measureQuestions)

    const medicalQuestions = toMedicalQuestions(questionnaire)
    questions = questions.concat(medicalQuestions)

    return {
      title: 'VITRAD04',
      quotationId,
      questionaries: [
        {
          riskNumber: insured.riskNumber,
          questionaries: [
            {
              questionnaireType: 'VITRAD04',
              language: 'ESPA',
              questions
            }
          ]
        }
      ]
    }
  }

  static fromGetRecoveryResponse(questionnaire: any[]): {
    weight: number | null
    height: number | null
    questionnaire: LifeInsuredMedicalQuestionnaire
  } {
    if (!questionnaire || questionnaire?.length === 0) {
      return {
        weight: null,
        height: null,
        questionnaire: new LifeInsuredMedicalQuestionnaire()
      }
    }

    const questions = questionnaire[0].questions

    //* First page
    const measuresQuestions = questions.filter((q: any) =>
      MEASURE_QUESTIONS.includes(q.questionCode)
    )
    const { weight, height } = fromMeasureQuestions(measuresQuestions)

    //* Second page
    const medicalQuestions = questions.filter((q: any) =>
      MEDICAL_QUESTIONS.includes(q.questionCode)
    )
    const questionnaireData = fromMedicalQuestions(medicalQuestions)

    return { weight, height, questionnaire: questionnaireData }
  }
}

// TO ---------------------
function responseYesNo(value: boolean) {
  return value ? 'S' : 'N'
}

function toMeasureQuestions(userDetails: UserDetails) {
  return [
    new AssociatedQuestionnaire(WEIGHT_QUESTION, 'INTEGER', String(userDetails.weight)),
    new AssociatedQuestionnaire(HEIGHT_QUESTION, 'INTEGER', String(userDetails.height))
  ]
}

function toMedicalQuestions(q: LifeInsuredMedicalQuestionnaire) {
  const questions = []
  questions.push(fromDescriptionValue(DISABILITY_QUESTION, q.disability))
  questions.push(fromDateAndReason(INTERRUPTED_ACTIVITY_QUESTION, q.interruptedActivity))
  questions.push(fromDescriptionValue(SICK_LEAVE_QUESTION, q.sickLeave))
  questions.push(fromUsageValue(SMOKING_QUESTION, q.smoking))
  questions.push(fromUsageValue(ALCOHOL_QUESTION, q.alcohol))
  questions.push(fromUsageValue(DRUGS_QUESTION, q.drugs))
  questions.push(fromDescriptionValue(MEDICATION_QUESTION, q.medication, 'ANEX001'))
  questions.push(fromPathologyValue(INJURIES_QUESTION, q.injuries))

  return questions
}

function fromDescriptionValue(
  questionCode: string,
  value: QuestionDescription,
  questionId: string = 'ANEX01'
) {
  const isAnswered = !value.isEmpty()
  const associatedQuestionnaires = [
    new AssociatedQuestionnaire(questionId, 'TEXTAREA', value.description)
  ]

  return {
    questionCode,
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(isAnswered),
        responseText: null,
        associatedQuestionnaires: isAnswered ? associatedQuestionnaires : null
      }
    ]
  }
}

function fromDateAndReason(questionCode: string, value: QuestionAdmission) {
  const isAnswered = !value.isEmpty()
  const associatedQuestionnaires = [
    new AssociatedQuestionnaire('ANEX001', 'DATE', value.date),
    new AssociatedQuestionnaire('ANEX002', 'TEXTAREA', value.reason)
  ]

  return {
    questionCode,
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(isAnswered),
        responseText: null,
        associatedQuestionnaires: isAnswered ? associatedQuestionnaires : null
      }
    ]
  }
}

function fromUsageValue(questionCode: string, value: QuestionUsage) {
  const isAnswered = !value.isEmpty()
  const associatedQuestionnaires = [
    new AssociatedQuestionnaire('ANEX001', 'INTEGER', value.amount),
    new AssociatedQuestionnaire('ANEX002', 'TEXTAREA', value.type),
    new AssociatedQuestionnaire('ANEX003', 'TEXTAREA', value.frequency)
  ]

  return {
    questionCode,
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(isAnswered),
        responseText: null,
        associatedQuestionnaires: isAnswered ? associatedQuestionnaires : null
      }
    ]
  }
}

function fromPathologyValue(questionCode: string, value: QuestionPathology) {
  const isAnswered = !value.isEmpty()
  const associatedQuestionnaires = [
    new AssociatedQuestionnaire('ANEX01', 'DATE', value.date),
    new AssociatedQuestionnaire('ANEX02', 'TEXTAREA', value.pathology),
    new AssociatedQuestionnaire('ANEX04', 'TEXTAREA', value.center)
  ]

  return {
    questionCode,
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(isAnswered),
        responseText: null,
        associatedQuestionnaires: isAnswered ? associatedQuestionnaires : null
      }
    ]
  }
}

// FROM ---------------------
function fromMeasureQuestions(questions: any[]) {
  const weight = questions[0].responses[0].value
  const height = questions[1].responses[0].value

  return { weight: Number(weight), height: Number(height) }
}

function fromMedicalQuestions(questions: any[]): LifeInsuredMedicalQuestionnaire {
  const disabilityQuestion = findQuestionBy(questions, 'P003')
  const disability = getDescriptionValue(disabilityQuestion)

  const interruptedActivityQuestion = findQuestionBy(questions, 'P004')
  const interruptedActivity = getDateAndReason(interruptedActivityQuestion)

  const sickLeaveQuestion = findQuestionBy(questions, 'P005')
  const sickLeave = getDescriptionValue(sickLeaveQuestion)

  const smokeQuestion = findQuestionBy(questions, 'P006')
  const smoking = getUsageValue(smokeQuestion)

  const alcoholQuestion = findQuestionBy(questions, 'P007')
  const alcohol = getUsageValue(alcoholQuestion)

  const drugsQuestion = findQuestionBy(questions, 'P008')
  const drugs = getUsageValue(drugsQuestion)

  const medicationQuestion = findQuestionBy(questions, 'P009') // ?
  const medication = getDescriptionValue(medicationQuestion)

  const injuriesQuestion = findQuestionBy(questions, 'P044')
  const injuries = getPathologyValue(injuriesQuestion)

  return new LifeInsuredMedicalQuestionnaire({
    disability,
    interruptedActivity,
    sickLeave,
    smoking,
    alcohol,
    drugs,
    medication,
    injuries,
    filled: true
  })
}

function findQuestionBy(questions: any[], id: string) {
  return questions.find((q: any) => String(q.questionCode) === id)
}

function getDescriptionValue(question: any) {
  const answers = question?.responses?.[0]?.associatedQuestionnaires
  const description = answers?.[0]?.responses?.[0]?.value ?? null
  return new QuestionDescription({ description })
}

function getDateAndReason(question: any) {
  const answers = question.responses?.[0]?.associatedQuestionnaires

  const date = answers?.[0]?.responses?.[0]?.value
  const reason = answers?.[1]?.responses?.[0]?.value

  return new QuestionAdmission({ date, reason })
}

function getUsageValue(question: any) {
  const answers = question.responses?.[0]?.associatedQuestionnaires

  const amount = answers?.[0]?.responses?.[0]?.value
  const type = answers?.[1]?.responses?.[0]?.value
  const frequency = answers?.[2]?.responses?.[0]?.value

  return new QuestionUsage({
    amount,
    type,
    frequency
  })
}

function getPathologyValue(question: any) {
  const answers = question.responses?.[0]?.associatedQuestionnaires

  const date = answers?.[0]?.responses?.[0]?.value
  const pathology = answers?.[1]?.responses?.[0]?.value
  const center = answers?.[2]?.responses?.[0]?.value

  return new QuestionPathology({
    date,
    pathology,
    center
  })
}
